import { useEffect, useRef } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { PlacementType, } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useDefaultApiParams } from './useDefaultApiParams';
import { parseCategories, } from 'src/pages/storefrontPage/utils';
import { useAnalyticsContext } from 'src/common/metrics';
/**
 * Fetches products for all DEALS storefront categories in parallel.
 * The page shows a loading state until the first category resolves,
 * while the remaining categories load in the background for instant tab switches.
 */
export const useDealsStorefront = (options) => {
    var _a, _b, _c;
    const [searchParams] = useSearchParams();
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    const isEnabled = (_a = options.enabled) !== null && _a !== void 0 ? _a : true;
    const { id } = options.storefront;
    const storefrontMeta = options.storefront.meta;
    const { storefrontCategories, invalidCategories } = parseCategories(storefrontMeta, searchParams);
    // Analytics — fire once when enabled becomes true
    const hasLoggedRef = useRef(false);
    useEffect(() => {
        if (!isEnabled || hasLoggedRef.current)
            return;
        hasLoggedRef.current = true;
        if (invalidCategories.length > 0) {
            rumClient.recordEvent('INVALID_STOREFRONT_CATEGORIES', {
                storefrontId: id,
                invalidCategories: invalidCategories.join(','),
                url: window.location.href,
            });
        }
        if (storefrontCategories.length === 0) {
            rumClient.recordEvent('EMPTY_STOREFRONT_CATEGORIES', { storefrontId: id, url: window.location.href });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled]);
    const categoryResults = useQueries({
        queries: storefrontCategories.map((category) => ({
            queryKey: ['getStorefrontProducts', storefrontMeta.strategy, category],
            queryFn: () => {
                const defaultApiParams = getDefaultParams();
                return HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy: storefrontMeta.strategy, category }, defaultApiParams));
            },
            staleTime: Infinity,
            enabled: isEnabled,
        })),
    });
    return {
        isLoading: isEnabled && categoryResults.length > 0 ? ((_c = (_b = categoryResults[0]) === null || _b === void 0 ? void 0 : _b.isLoading) !== null && _c !== void 0 ? _c : true) : false,
        storefrontCategories,
        categoryResults,
    };
};
/**
 * REGULAR storefront — fetches product widget by ASINs. No tabs.
 */
export const useRegularStorefront = (options) => {
    var _a, _b;
    const { getDefaultParams } = useDefaultApiParams();
    const isEnabled = (_a = options.enabled) !== null && _a !== void 0 ? _a : true;
    const storefrontMeta = options.storefront.meta;
    const hasProducts = ((_b = storefrontMeta.products) === null || _b === void 0 ? void 0 : _b.length) > 0;
    return useQuery({
        queryKey: ['getProductWidget', storefrontMeta.products],
        queryFn: () => {
            const defaultApiParams = getDefaultParams();
            return HandshakePainterServiceApiV1.getProductWidget(Object.assign({ asins: storefrontMeta.products, placement: PlacementType.CustomersAlsoBought }, defaultApiParams));
        },
        enabled: isEnabled && hasProducts,
    });
};
